/**
 * Exportação de listas.
 *
 * Três formatos, três decisões:
 *
 *  · TEXTO — vai para o WhatsApp, então usa a formatação que o app entende
 *    (*negrito*) e evita tabelas, que viram lixo em tela estreita. Copia para a
 *    área de transferência e, onde houver, oferece o compartilhamento nativo do
 *    aparelho.
 *
 *  · PLANILHA — CSV com separador ponto-e-vírgula e BOM UTF-8. O Excel em
 *    português abre CSV com duplo clique e respeita o ponto-e-vírgula como
 *    separador; sem o BOM ele estraga os acentos. Gerar .xlsx de verdade exigiria
 *    ~400 KB de biblioteca para o mesmo resultado prático.
 *
 *  · PDF — via impressão do navegador, que todo sistema oferece como "Salvar
 *    como PDF". Evita outra biblioteca pesada e usa a folha de estilo de
 *    impressão, então o resultado sai limpo, sem menu nem botões.
 */

/** Baixa um arquivo gerado no navegador. */
function baixar(conteudo: BlobPart, nome: string, tipo: string): void {
  const url = URL.createObjectURL(new Blob([conteudo], { type: tipo }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  a.click();
  URL.revokeObjectURL(url);
}

const hoje = (): string => new Date().toISOString().slice(0, 10);

/** Escapa um campo de CSV: aspas dobradas e o campo entre aspas quando precisa. */
function campoCsv(v: unknown): string {
  const s = v === null || v === undefined ? '' : String(v);
  return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function exportarPlanilha(
  colunas: { chave: string; titulo: string }[],
  linhas: Record<string, unknown>[],
  nomeBase: string,
): void {
  const cabecalho = colunas.map((c) => campoCsv(c.titulo)).join(';');
  const corpo = linhas.map((l) => colunas.map((c) => campoCsv(l[c.chave])).join(';'));
  // ﻿ é o BOM: sem ele o Excel lê os acentos errado.
  const csv = `﻿${[cabecalho, ...corpo].join('\r\n')}`;
  baixar(csv, `${nomeBase}-${hoje()}.csv`, 'text/csv;charset=utf-8');
}

export interface ResultadoCopia {
  ok: boolean;
  compartilhado: boolean;
}

/**
 * Copia o texto e, no celular, abre o compartilhamento nativo — o caminho mais
 * curto até o WhatsApp.
 */
export async function exportarTexto(texto: string, titulo: string): Promise<ResultadoCopia> {
  // `navigator.share` só existe em contexto seguro e em parte dos aparelhos.
  if (typeof navigator !== 'undefined' && 'share' in navigator) {
    try {
      await navigator.share({ title: titulo, text: texto });
      return { ok: true, compartilhado: true };
    } catch {
      // Usuário cancelou ou o aparelho recusou: cai para a cópia.
    }
  }

  try {
    await navigator.clipboard.writeText(texto);
    return { ok: true, compartilhado: false };
  } catch {
    return { ok: false, compartilhado: false };
  }
}

/** Aciona a impressão. O usuário escolhe "Salvar como PDF" no diálogo. */
export function exportarPdf(): void {
  window.print();
}
